import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import { Cell, CellWithHierarchy } from '@pelckmans/elementis/components/table';
import CalendarIcon from '@pelckmans/elementis/icons/calendar';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { isMyAssignment } from '../../../../../api/assignment';
import { Module } from '../../../../../api/module';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../constants/analyticsEvents';
import useAnalytics from '../../../../../hooks/useAnalytics';
import HorizontalScrollContextProps from '../../../../../shared-components/table/context/HorizontalScrollContext';
import { buildGroupProps } from '../../../../../utils/elementisUtils';
import { formatAssignmentToDate, formatDate } from '../../../../../utils/formatDate';
import { getFullHierarchyDescriptionOfNode } from '../../../../../utils/getNodeDescription';
import { MaterialIcon } from '../../../../material/components/MaterialIcon';
import ScorableProgress from '../../../../material/toc/components/ScorableProgress';
import useAssignmentHandling from '../../../../material/toc/hooks/assignments/useAssignmentHandling';
import type { FinishedStudentAssignment } from '../api/finished-student-assignment';
import type { StudentAssignment } from '../hooks/useStudentAssignments';
import { EndDateCategory } from './AssignmentDivision';

const BASE = 'PAGES.ASSIGNMENTS.STUDENT.SECTION.TABLE.BODY.ASSIGNMENT';

type AssignmentProps = {
  assignment: StudentAssignment;
  endDateCategory: EndDateCategory;
  finished?: boolean;
  fetchAssignments: () => Promise<void>;
  assignmentModule: Module;
  disabled?: boolean;
};

type PeriodCellProps = {
  assignment: StudentAssignment;
  endDateCategory: EndDateCategory;
};

const PeriodCell = ({ assignment, endDateCategory }: PeriodCellProps) => {
  const { t } = useTranslation();

  const from = new Date(assignment.from);

  const pillProminence =
    endDateCategory === 'TODAY' ? 'danger' : endDateCategory === 'THIS_WEEK' ? 'warning' : 'neutral';

  const period =
    from < new Date()
      ? t(`${BASE}.PERIOD.TO`, { to: formatAssignmentToDate(assignment.to) })
      : t(`${BASE}.PERIOD.FROM_TO`, {
          from: formatAssignmentToDate(assignment.from),
          to: formatAssignmentToDate(assignment.to),
        });

  return (
    <Cell
      pillGroupProps={{
        'aria-label': t(`${BASE}.PERIOD.A11Y_LABEL`),
        role: 'group',
      }}
      minWidth="sm"
      maxWidth="sm"
    >
      <Cell.Pill prominence={pillProminence}>{period}</Cell.Pill>
    </Cell>
  );
};

const SubmittedOnCell = ({ assignment }: { assignment: StudentAssignment }) => {
  const date = formatDate(assignment.score.achievedAt || '');

  return (
    <Cell minWidth="sm" maxWidth="sm">
      {date !== '' && <Cell.Text title={date} startAdornment={<CalendarIcon data-testid="calendar-icon" />} />}
    </Cell>
  );
};

export default function Assignment({
  assignment,
  endDateCategory,
  finished,
  fetchAssignments,
  assignmentModule,
  disabled = false,
}: AssignmentProps) {
  const { t } = useTranslation();
  const analytics = useAnalytics();

  const { horizontalScroll } = useContext(HorizontalScrollContextProps);

  const { startAssignment, resumeAssignment, restartAssignment, reviewAssignment } = useAssignmentHandling({
    onAssignmentClosed: fetchAssignments,
    subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_STUDENT_ASSIGNMENTS,
    assignmentModule,
  });

  const completedAssignment = Boolean(
    (assignment.score.status === 'finished' || assignment.score.status === 'pending-grading') &&
      assignment.score.achievedAt,
  );

  const restartableAssignment = Boolean(completedAssignment && assignment.repeatable);

  const resumableAssignment = Boolean(
    assignment.score.achievedAt &&
      assignment.score.status !== 'finished' &&
      assignment.score.status !== 'pending-grading',
  );

  const locationIndicator = getFullHierarchyDescriptionOfNode(assignment.hierarchy, t);

  const accessMaterialEvent = () => {
    analytics.capture({
      eventType:
        assignment.materialType === 'medialink'
          ? ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED
          : ANALYTICS_EVENT_TYPES.USER_MATERIAL_ACCESSED,
      objectId: assignment.materialId,
      subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_STUDENT_ASSIGNMENTS,
    });
  };

  const handleRestart = async () => {
    if (isMyAssignment(assignment)) {
      await restartAssignment(assignment);
      accessMaterialEvent();
    }
  };

  const handleResume = async () => {
    if (isMyAssignment(assignment)) {
      await resumeAssignment(assignment);
      accessMaterialEvent();
    }
  };

  const handleReview = async () => {
    await reviewAssignment(assignment as FinishedStudentAssignment);
  };

  const handleStart = async () => {
    if (isMyAssignment(assignment)) {
      await startAssignment(assignment);
      accessMaterialEvent();
    }
  };

  return (
    <tr className="table-item">
      <CellWithHierarchy
        scope="row"
        hierarchyProps={{ level: 1, hasCollapsibleParent: true }}
        minWidth="xl"
        maxWidth="xl"
      >
        <Cell.Text
          title={assignment.name}
          startAdornment={<MaterialIcon materialType={assignment.materialType} contentType={assignment.contentType} />}
        />
      </CellWithHierarchy>
      <Cell minWidth="md" maxWidth="md">
        {assignmentModule && (
          <Cell.Text
            title={locationIndicator}
            subTitle={{
              text: assignmentModule.name,
              position: 'bottom',
              startAdornment: (
                <Avatar>
                  <AvatarImage name={assignmentModule.niceName} src={assignmentModule.styling?.logo || ''} />
                </Avatar>
              ),
            }}
          />
        )}
      </Cell>
      {finished ? (
        <SubmittedOnCell assignment={assignment} />
      ) : (
        <PeriodCell assignment={assignment} endDateCategory={endDateCategory} />
      )}
      <Cell className="results-cell" minWidth="xs" maxWidth="xs">
        <span className="results-wrapper">
          <ScorableProgress
            unachievable={isMyAssignment(assignment) && assignment.unachievable}
            score={assignment.score}
            variant="donut"
          />
        </span>
      </Cell>
      <Cell minWidth="xs" maxWidth="xs">
        <Cell.Text title={assignment.repeatable ? t(`${BASE}.ATTEMPTS.MULTIPLE`) : t(`${BASE}.ATTEMPTS.ONE`)} />
      </Cell>
      <Cell sticky={horizontalScroll} buttonGroupProps={{ ...buildGroupProps(<></>), className: 'actions-cell' }}>
        {finished ? (
          <Cell.Button prominence="neutral" onClick={handleReview} disabled={disabled}>
            {t(`${BASE}.ACTIONS.REVIEW`)}
          </Cell.Button>
        ) : restartableAssignment ? (
          <Cell.Button prominence="neutral" onClick={handleRestart} disabled={disabled}>
            {t(`${BASE}.ACTIONS.RESTART`)}
          </Cell.Button>
        ) : resumableAssignment ? (
          <Cell.Button prominence="neutral" onClick={handleResume} disabled={disabled}>
            {t(`${BASE}.ACTIONS.RESUME`)}
          </Cell.Button>
        ) : (
          <Cell.Button prominence="neutral" onClick={handleStart} disabled={disabled}>
            {t(`${BASE}.ACTIONS.START`)}
          </Cell.Button>
        )}
      </Cell>
    </tr>
  );
}
